import React, {useEffect, useRef, useState} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {useQueryClient} from 'react-query'
import {default as Select} from "react-select";
import {REPORTER_USER_LEVEL, useSolv, useTenant} from "./components/SolvProvider"
import {getAllLangOpts, getLangOpt, useIntlEx} from "./components/IntlUtils.js"
import MainMenu from "./MainMenu.js"
import {
  ActionButton,
  DropdownMenu,
  FormBody,
  FormGroup,
  FormHeader,
  MainContainer,
  useAlert
} from "./components/FormComps.js"
import {
  clearAllErrors,
  clearError,
  InfoBlock,
  InfoText,
  validateEmailAddress,
  validateField
} from './components/ValidationUtils.js';
import {AccountTabs, mkHref} from "./components/AccountUtils";
import {useAccessLevelOpts, UserStatus, useThemeOpts} from "./components/UserUtils";
import {ImageDropBox, processImageFile, uploadImage} from "./components/ImageUtils";
import ImageDialog from "./components/ImageDialog";
import {shortIdEqual} from "./components/StringUtils";
import {isNullOrUndefined} from "./components/ReactUtils";
import getLogger from "./components/Logging.js"
import './App.css'

const log = getLogger("MemberEdit")

export default function MemberEdit(props) {

  const {env, api, session: {user}, refreshSignin, setFatal, setBusy} = useSolv()
  const {intl} = useIntlEx()

  const location = useLocation()
  const params = useParams()

  const {tenant} = useTenant(params.tenantId)

  const [userId, setUserId] = useState(params.userId)

  // const [editMode, setEditMode] = useState(params.userId === "_new" ? "ADD" : params.userId === "_edit" ? "EDIT" : null)
  const [editMode, setEditMode] = useState((!params.userId) ? "ADD" : (location.pathname.endsWith("_edit") ? "EDIT" : null))
  const [touched, setTouched] = useState(false)
  const [alert, setAlert] = useAlert()

  const {findAccessLevelOpt, findInvitableAccessLevelOpts} = useAccessLevelOpts()
  const {themeOpts, findThemeOpt} = useThemeOpts()

  const [row, setRow] = useState(null)
  const [fullName, setFullName] = useState(null)
  const [emailAddress, setEmailAddress] = useState(null)
  const [displayName, setDisplayName] = useState(null)
  const [userLevel, setUserLevel] = useState(null)
  const [oldUserLevel, setOldUserLevel] = useState(null)
  const [oldEmailAddress, setOldEmailAddress] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [lang, setLang] = useState(null)
  const [themeOpt, setThemeOpt] = useState(null)
  const [timezone, setTimezone] = useState(null)
  const [idProvider, setIdProvider] = useState(null)
  const [signedInOn, setSignedInOn] = useState(null)
  const [status, setStatus] = useState(null)
  const [createdById, setCreatedById] = useState(null)

  const [accessLevelOpts, setAccessLevelOpts] = useState(null)

  const imagePickerDialogRef = useRef(null)
  const [imageInfo, setImageInfo] = useState(null)

  const queryClient = useQueryClient()

  useEffect(() => {

    // // log.debug("useEffect: invoked")

    try {
      if ("ADD" === editMode) {
        if (tenant) {
          const a = findInvitableAccessLevelOpts(tenant, user.userLevel)
          if (a.length === 0) {
            setUserLevel(findAccessLevelOpt(REPORTER_USER_LEVEL))
          }
          else if (a.length >= 1) {
            setAccessLevelOpts(a)
            setUserLevel(a[0])
          }
        }
      } else {
        if (!userId) {
          setUserId(user.userId)
        }
        if (tenant) {
          setBusy(intl.msg("loading"))
          setAccessLevelOpts(findInvitableAccessLevelOpts(tenant, user.userLevel))
          loadMember()
            .catch((error) => {
              setFatal(error)
            })
            .finally(function () {
              setBusy(null)
            })
        }
      }
    } catch (error) {
      // log.debug("FATAL", error)
      setFatal(error)
    } finally {
      setBusy(null)
    }

  }, [tenant]);

  function loadMember() {
    // // log.debug("Loading member")
    return api.getUser(tenant.tenantId, userId)
      .then(({data}) => {
        // log.debug("User loaded: data=", data, findThemeOpt(data.theme || "system"))
        setRow(data)
        setFullName(data.fullName)
        setDisplayName(data.displayName)
        setEmailAddress(data.emailAddress)
        setOldEmailAddress(data.emailAddress)
        setUserLevel(findAccessLevelOpt(data.userLevel))
        setOldUserLevel(findAccessLevelOpt(data.userLevel))
        setImageUrl(data.imageUrl)
        setLang(getLangOpt(data.lang ? data.lang : "en"))
        if (!data.theme.startsWith("http")) {
          setThemeOpt(findThemeOpt(data.theme || "system"))
        }
        else {
          setThemeOpt({value: data.theme, label: data.theme})
        }
        setIdProvider(data.idProvider)
        setStatus(data.status)
        setCreatedById(data.createdById?.replace(/\-/g, ""))
        return data
      })
      .catch((error) => {
        // log.debug("Load User failed: error=", error)
        setFatal(error)
      })
  }

  function handleFullNameChange(e) {
    setFullName(e.target.value)
    setTouched(true)
  }

  function handleEmailAddressChange(e) {
    setEmailAddress(e.target.value)
    setTouched(true)
  }

  function handleUserLevelChange(v) {
    setUserLevel(v)
    setTouched(true)
  }

  function handleLangChange(v) {
    setLang(v)
    setTouched(true)
  }

  function handleThemeOptChange(v) {
    setThemeOpt(v)
    setTouched(true)
  }

  async function handleImageUrlChange(e) {

    if (e.target.files && e.target.files.length > 0) {
      let file = e.target.files[0]
      let result

      setBusy(intl.msg("processing"))

      try {
        result = await processImageFile({
          file: file,
          maxFileSize: 100_000,
          width: 150,
          height: 150,
        })
      }
      catch (ex) {
        setAlert({type: "error", text: "error_image_process_failed"})
      }
      finally {
        setBusy(null)
      }

      if (result.warning || result.error) {
        setImageInfo(result)
        const el = document.getElementById("inp_dlg_logoImagePicker")
        el.click()
      }
      else {
        setImageUrl(result.dataUrl)
        setTouched(true)
      }

      clearError("inp_logoUrl")
    }
  }

  function handleImageUrlClear() {
    setImageUrl(null)
    setTouched(true)
  }

  function validate() {

    let opt = {focusEl: null, valid: true}

    clearAllErrors("frmMain")

    validateField("inp_emailAddress", emailAddress && validateEmailAddress(emailAddress), "valid", opt)

    if (!isAdding()) {
      validateField("inp_fullName", fullName && fullName.trim().length > 0, "required", opt)
    }

    validateField("inp_accessLevel", userLevel, "required", opt)

    // // log.debug("VALIDATE: opt=", opt)

    if (opt.focusEl) {
      document.getElementById(opt.focusEl).focus()
    }

    return opt.valid

  }

  function emailAddressChanged() {
    return (emailAddress.trim().toLowerCase() !== oldEmailAddress.trim().toLowerCase())
  }

  async function handleSaveClick() {

    // // log.debug("handleSaveClick: invoked" )

    if (validate()) {

      setBusy(intl.msg("saving"))

      try {

        if (isAdding()) {

          const payload = {
            emailAddress: emailAddress,
            accessLevel: userLevel.value,
            invokedById: tenant.accessingAs("*/INSTREAM/*") ? user.userId : tenant.owner.userId
          }

          // log.debug("handleSaveClick: payload=", payload)

          const {data} = await api.insertUser(tenant.tenantId, payload)

          // log.debug("handleSaveClick: res=", data)

          window.location = mkHref({suffix: `/members`, tenant: tenant, user: user})

        }
        else {

          let finalImageUrl = imageUrl

          if (finalImageUrl && finalImageUrl.startsWith("data:")) {
            finalImageUrl = await uploadImage({
              api: api,
              env: env,
              imageData: finalImageUrl,
              imagePath: `u/${userId}/avatar`,
              setImageUrl: setImageUrl,
            })
          }

          const payload = {
            emailAddress: emailAddress,
            fullName: fullName,
            userLevel: userLevel.value,
            imageUrl: finalImageUrl,
            lang: lang.value,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            theme: themeOpt.value,
          }

          await api.updateUser(tenant.tenantId, row.userId, payload)

          if (isMe()) {

            let prefs = {
              lang: null,
              theme: themeOpt.value
            }

            if (lang.value !== "system") {
              prefs.lang = lang.value
            }

            await refreshSignin()

          }

          window.location = mkHref({suffix: `/members`, tenant: tenant, user: user})

        }

      }
      catch (error) {
        handleError(error)
      }
      finally {
        setBusy(null)
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_validation")})
    }

  }

  function handleEditClick() {
    if (canEdit()) {
      setEditMode("EDIT")
      const fc = document.querySelector(".form-control")
      // // log.debug(">>>Z: fc=", fc, fc.focus())
      if (fc) {
        fc.disabled = false
        fc.focus()
      }
    }
  }

  function resetEditMode() {
    if (canEdit()) {
      setEditMode(null)
      setTouched(false)
    }
  }

  function handleCancelClick() {
    // resetEditMode()
    // window.location.reload()
    window.location = mkHref({suffix: `/members`, tenant: tenant, user: user})
  }

  function handleError(error) {
    if (error && error.code) {
      // // log.debug(">>>Z: handleError", error.code)
      const code = error.code
      const details = error.details
      switch (code) {
        case "DUPLICATE_USER":
        case "DUPLICATE_KEY":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_user"), field: "inp_emailAddresses", constraint: "valid"})
          break
        case "BANNED_INVITER":
          setAlert({type: "error", text: intl.msg("error_banned_user", details)})
          break
        case "UNKNOWN_INVITER":
        case "INVALID_INVITER":
          setAlert({type: "error", text: intl.msg("invite_error_invalid_inviter", details), field: "inp_emailAddress", constraint: "valid"})
          break
        case "DUPLICATE_INVITEE_USER":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_user", details), field: "inp_emailAddress", constraint: "unique"})
          break
        case "DUPLICATE_INVITEE_TENANT":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_tenant", details), field: "inp_tenantName", constraint: "unique"})
          break
        case "BANNED_EMAIL_ADDRESS":
          setAlert({type: "error", text: intl.msg("error_banned_email_address", details), field: "inp_emailAddress", constraint: "valid"})
          break
        default:
          setAlert({type: "error", text: intl.msg("error_failed", details)})
          break
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  function canEdit(canChange) {
    if (!["undefined", "null"].includes(typeof(canChange)) && !canChange) {
      return false
    }
    if (isInvitation()) {
      return tenant.accessingAs("MANAGER/INSTREAM,DOWNSTREAM/*") || shortIdEqual(createdById, user.userId)
    }
    if (isMe()) {
      return true
    }
    if (row) {
      return (tenant.accessingAt(row.userLevel))
    }
    else {
      return false
    }
  }

  function isEditing(canChange) {
    if (isNullOrUndefined(canChange)) {
      return ("ADD" === editMode || "EDIT" === editMode)
    }
    else {
      return (("ADD" === editMode) || ("EDIT" === editMode && canEdit(canChange)))
    }
  }

  function isInvitation() {
    return "INVITING" === row?.status
  }

  function isAdding() {
    return ("ADD" === editMode)
  }

  function isMe() {
    return user.userId.replace(/\-/g, "") === row?.userId?.replace(/\-/g, "")
  }

  function isOwner() {
    return (row?.userLevel === 100)
  }

  function canChangeEmailAddress() {
    return (isMe() || tenant.accessingAs("MANAGER/INSTREAM,DOWNSTREAM/*"))
  }

  function canChangeFullName() {
    return (isMe() || tenant.accessingAs("MANAGER/INSTREAM,DOWNSTREAM/*"))
  }

  function canChangeUserLevel() {
    if (isOwner()) {
      return (canChangeOwner())
    }
    else {
      return (tenant.accessingAs("MANAGER/INSTREAM,DOWNSTREAM/*"))
    }
  }

  function canChangeOwner() {
    return (isOwner() && tenant.accessingAs(["OWNER/INSTREAM/", "ADMINISTRATOR/DOWNSTREAM/RESELLER,SYSTEM"]))
  }

  function MoreMenu(props) {

    const comps = []

    if (!touched) {

    }

    return (
      <>
        {
          comps.length > 0 &&
            <DropdownMenu>
              {comps}
            </DropdownMenu>
        }
      </>
    )
  }

  function FormControls(props) {
    const cmps = []

    cmps.push(
      <ActionButton name={isAdding() ? "add_new" : "save"} flavor="primary" disabled={!isInvitation() && !touched} onClick={handleSaveClick}/>
    )
    cmps.push(
      <ActionButton name="cancel" onClick={handleCancelClick}/>
    )
    cmps.push(
      <MoreMenu/>
    )
    return cmps
  }

return (
  tenant &&
      <>

        <MainContainer tenant={tenant} menu={MainMenu}>

          <FormHeader>
            <AccountTabs tenant={tenant}/>
            <FormHeader.Toolbar>
              <FormHeader.Toolbar.Title>
                {
                  isAdding() ? (
                    <>
                      <div>{intl.msg("member")}</div>
                      <div className="text-muted">-</div>
                      <div>(new)</div>
                    </>
                  ) : (
                    <>
                      <div>{intl.msg("member")}</div>
                      <div className="text-muted">-</div>
                      <div>{displayName}</div>
                      <UserStatus row={row} size="lg" showYou={true}/>
                    </>
                  )
                }
              </FormHeader.Toolbar.Title>
              <FormHeader.Toolbar.Controls>
                <FormControls/>
              </FormHeader.Toolbar.Controls>
            </FormHeader.Toolbar>
            <FormHeader.Alert alert={alert}/>
          </FormHeader>

          <FormBody>

            <div className="row">
              <div className={`col-sm-${isInvitation() || isAdding() ? "12" : "8"}`}>

                <FormGroup hidden={!isEditing(canChangeFullName())}>
                  <FormGroup.Label text={intl.msg("member_name")} description={intl.msg("member_name_description")} htmlFor="inp_fullName"/>
                  <FormGroup.Control>
                    <div style={{maxWidth: "500px"}}>
                      <input id="inp_fullName" type="text" className="form-control" maxLength={150} value={fullName} disabled={!isEditing(canChangeFullName())} onChange={handleFullNameChange}/>
                    </div>
                    <InfoBlock>
                      <InfoText validate="required" disabled={!isEditing(canChangeFullName())}>{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>

                <FormGroup disabled={!isEditing(canChangeEmailAddress())}>
                  <FormGroup.Label text={intl.msg(`email_address`)} description={intl.msg(`member_${isAdding() ? "invite_" : ""}email_address_description`)} htmlFor="inp_emailAddress"/>
                  <FormGroup.Control>
                    <div style={{maxWidth: "500px"}}>
                      <input id="inp_emailAddress" type="text" className="form-control" maxLength={150} value={emailAddress} disabled={!isEditing(canChangeEmailAddress())} onChange={handleEmailAddressChange}/>
                    </div>
                    <InfoBlock>
                      <InfoText validate="unique" disabled={!isEditing()}>{intl.msg("member_email_address_helptext_unique")}</InfoText>
                      <InfoText validate="valid" disabled={!isEditing()}>{intl.msg("helptext_email_address_valid")}</InfoText>
                      <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>

                <FormGroup hidden={!accessLevelOpts || accessLevelOpts.length <= 0} disabled={!isEditing(canChangeUserLevel())}>
                  <FormGroup.Label text={intl.msg("member_access_level")} description={intl.msg("member_access_level_description")} htmlFor="inp_accessLevel"/>
                  <FormGroup.Control>
                    <div style={{maxWidth: "500px"}}>
                      <Select
                        id="inp_accessLevel"
                        className="react-select"
                        classNamePrefix="react-select"
                        cacheOptions
                        value={userLevel}
                        options={accessLevelOpts}
                        // placeholder={null}
                        isSearchable={false}
                        // hideSelectedOptions={true}
                        onChange={handleUserLevelChange}
                        isDisabled={!isEditing(canChangeUserLevel())}
                      />
                    </div>
                    <InfoBlock>
                      {/*{*/}
                      {/*  isEditing() && isOwner() && !isInvitation() &&*/}
                      {/*    <InfoText warning={true} >{intl.msg("member_access_level_helptext_owner")}</InfoText>*/}
                      {/*}*/}
                      <InfoText validate="required" disabled={!isEditing(canChangeUserLevel())}>{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>

                {
                  (!isInvitation() && !isAdding() && (isOwner() || isMe() || tenant.accessingAs("MANAGER/*/SYSTEM"))) ? (
                    canEdit() ? (
                      <FormGroup disabled={!isEditing()}>
                        <FormGroup.Label text={intl.msg("member_lang")} description={intl.msg("member_lang_description")} htmlFor="inp_lang"/>
                        <FormGroup.Control>
                          <div style={{maxWidth: "500px"}}>
                            <Select
                              id="inp_lang"
                              className="react-select"
                              classNamePrefix="react-select"
                              cacheOptions
                              value={lang}
                              options={getAllLangOpts()}
                              // hideSelectedOptions={true}
                              onChange={handleLangChange}
                              isDisabled={!isEditing()}
                            />
                          </div>
                          <InfoBlock>
                            <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>
                          </InfoBlock>
                        </FormGroup.Control>
                      </FormGroup>
                    ) : (
                      <FormGroup disabled={!isEditing()}>
                        <FormGroup.Label text={intl.msg("member_lang")} description={intl.msg("member_lang_description")} htmlFor="inp_lang"/>
                        <FormGroup.Control>
                          <div style={{maxWidth: "500px"}}>
                            <input id="inp_lang" type="text" className="form-control" value={lang?.label} disabled={true}/>
                            <InfoBlock>
                              <InfoText validate="required">{intl.msg("helptext_optional")}</InfoText>
                            </InfoBlock>
                          </div>
                        </FormGroup.Control>
                      </FormGroup>
                    )
                  ) : (
                    <></>
                  )
                }

                {
                  !isInvitation() && !isAdding() && (!themeOpt?.value?.startsWith("http")) &&
                    <FormGroup disabled={!isEditing()}>
                      <FormGroup.Label text={intl.msg("member_theme")} description={intl.msg("member_theme_description")} htmlFor="inp_theme"/>
                      <FormGroup.Control>
                        <div style={{maxWidth: "500px"}}>
                          <Select
                            id="inp_theme"
                            className="react-select"
                            classNamePrefix="react-select"
                            cacheOptions
                            value={themeOpt}
                            options={themeOpts}
                            // hideSelectedOptions={true}
                            isSearchable={false}
                            onChange={handleThemeOptChange}
                            isDisabled={!isEditing()}
                          />
                        </div>
                        <InfoBlock>
                          <InfoText disabled={!isEditing()}>{intl.msg("helptext_optional")}</InfoText>
                        </InfoBlock>
                      </FormGroup.Control>
                    </FormGroup>
                }

              </div>

              {
                !isInvitation() && !isAdding() &&

                  <div className="col-sm-4">

                    <FormGroup>
                      <FormGroup.Label text={intl.msg("member_avatar")} description={intl.msg("member_avatar_description")} htmlFor="inp_avatar"/>
                      <FormGroup.Control>
                        <ImageDropBox
                          id="inp_avatar"
                          image={imageUrl}
                          imageClassName={"avatar"}
                          icon="person"
                          width={150}
                          height={150}
                          onChange={handleImageUrlChange}
                          onClear={handleImageUrlClear}
                          disabled={!isEditing()}/>
                        <InfoBlock>
                          <InfoText validate="res" disabled={!isEditing()}>{intl.msg("member_avatar_helptext_res")}</InfoText>
                          <InfoText validate="size" disabled={!isEditing()}>{intl.msg("member_avatar_helptext_size")}</InfoText>
                          <InfoText disabled={!isEditing()}>{intl.msg("helptext_optional")}</InfoText>
                        </InfoBlock>
                      </FormGroup.Control>
                    </FormGroup>

                  </div>
              }

            </div>

          </FormBody>

        </MainContainer>

        <ImageDialog
          ref={imagePickerDialogRef}
          id="dlg_logoImagePicker"
          title="Avatar Image"
          imageInfo={imageInfo}
          setTargetImageData={setImageUrl}
          setTouched={setTouched}/>

      </>

  )
}
